import React, { useCallback, useRef, useState } from 'react';

import { Map } from 'immutable';
import camelCase from 'just-camel-case';
import { connect } from 'react-redux';
import { EmployerProfile, Resource } from 'registration/types';

import { Button } from 'shared/components/ihcl/v2/button';
import { authenticityToken } from 'shared/helpers/rails';
import { validateRequired } from 'shared/helpers/validations';

import { getEmployerProfile } from '../reducers';
import EmployerProfilePreRegistationForm from './EmployerProfilePreRegistationForm';

type InitialErrors = {
  email?: boolean | string;
};

type EmployerFormProps = {
  employerProfile: EmployerProfile;
  initialErrors: InitialErrors;
  previousData: Partial<Resource>;
  urlEmail: string | null;
};

const EmployerForm: React.FC<EmployerFormProps> = ({
  employerProfile,
  initialErrors,
  previousData,
  urlEmail,
}) => {
  const signUpForm = useRef<HTMLFormElement>(null);

  const [companyName, setCompanyName] = useState(
    employerProfile?.company_name || ''
  );
  const [errors, setErrors] = useState(() => {
    const ie: InitialErrors = {};
    const initialErrorsData = initialErrors || {};
    if (initialErrorsData.email) {
      ie.email = initialErrorsData.email[0];
    }
    return Map(ie);
  });
  const [fullName, setFullName] = useState(
    (previousData?.type === 'employer' && previousData?.full_name) || ''
  );
  const [email, setEmail] = useState(
    employerProfile?.email ||
      (previousData?.type === 'employer' && previousData?.email) ||
      urlEmail ||
      ''
  );
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [phone, setPhone] = useState(
    (previousData?.type === 'employer' && previousData?.phone_and_extension) ||
      ''
  );
  const [jobTitle, setJobTitle] = useState('');

  const onTextFieldChange = useCallback((e) => {
    const target = e.currentTarget;
    const fieldName = camelCase(target.id);
    const { value } = target;

    setErrors((prevErrors) => prevErrors.delete(fieldName));

    switch (fieldName) {
      case 'companyName':
        setCompanyName(value);
        break;
      case 'fullName':
        setFullName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      case 'jobTitle':
        setJobTitle(value);
        break;
      default:
        break;
    }
  }, []);

  const onPasswordScoreChange = useCallback((score) => {
    setErrors((prevErrors) => prevErrors.delete('password'));
    setPasswordStrength(score);
  }, []);

  const ssoRequired = Boolean(employerProfile?.sso_required);

  const validateAndSubmit = useCallback(() => {
    const state = {
      companyName,
      fullName,
      email,
      password,
      phone,
      jobTitle,
    };

    const requiredFields = ssoRequired
      ? ['fullName', 'email', 'phone', 'jobTitle', 'companyName']
      : ['fullName', 'email', 'password', 'phone', 'jobTitle', 'companyName'];
    let validationErrors = validateRequired(state, ...requiredFields);

    if (!validationErrors.has('email') && email.indexOf('@') === -1) {
      validationErrors = validationErrors.set(
        'email',
        'Seems like there might be a problem here.'
      );
    }

    if (
      !ssoRequired &&
      !validationErrors.has('password') &&
      passwordStrength < 3
    ) {
      validationErrors = validationErrors.set(
        'password',
        'Password is too weak'
      );
    }

    if (validationErrors.size === 0) {
      signUpForm.current.submit();
    } else {
      setErrors(validationErrors);
    }
  }, [
    companyName,
    fullName,
    email,
    password,
    phone,
    jobTitle,
    passwordStrength,
    ssoRequired,
  ]);

  return (
    <div className="nopadding mx-auto w-full px-[15px]">
      <div className="top-buffer">
        <h2 className="align-center">
          Great talent quickly delivered to you &nbsp;
          <span className="face-with-sunglasses-emoji" />
        </h2>
      </div>
      <form action="/users/sign_up/employer" method="POST" ref={signUpForm}>
        <EmployerProfilePreRegistationForm
          companyName={companyName}
          email={email}
          employerProfile={employerProfile}
          errors={errors}
          fullName={fullName}
          jobTitle={jobTitle}
          onTextFieldChange={onTextFieldChange}
          onPasswordScoreChange={onPasswordScoreChange}
          password={password}
          phone={phone}
        />

        <div className="top-separator center-row">
          <input
            name="authenticity_token"
            type="hidden"
            value={authenticityToken()}
          />
          <input
            name="user[employer_profile_attributes][registered_as_employer]"
            type="hidden"
            value="1"
          />
          <Button
            variant="primary"
            className="get-started orange-button"
            onClick={validateAndSubmit}
          >
            GET STARTED
          </Button>
        </div>

        <div className="top-buffer padded-row center-row tos-row terms">
          <span>
            By signing up you agree to Incredible Health&apos;s{' '}
            <a
              href="https://www.incrediblehealth.com/terms.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.incrediblehealth.com/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </span>
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  employerProfile: getEmployerProfile(state),
});

export default connect(mapStateToProps)(EmployerForm);
